import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, inject, OnInit, TemplateRef, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';

import { ButtonModule, CommonMediaQueries, MediaScreenService, SpinnerDirective, SpinnerService } from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';

import { ConfigService, GlobalToastService, NewVersionCheckerService } from '@pp/core/services';
import { PwaSetupDialog } from '@pp/pwa-setup';

import { GoogleAnalyticsService } from './core/services/google-analytics.service';

@Component({
  imports: [RouterOutlet, AsyncPipe, SpinnerDirective, ButtonModule, TranslateModule],
  selector: 'pp-root',
  template: `
    <ng-container *puSpinner="spinner.isSpinning$ | async">
      @defer {
        @if (!isOldIos) {
          <router-outlet />
        } @else {
          <p>Some features may not work as expected. Please update your device.</p>
        }
      }
    </ng-container>

    <ng-template #newVersionTpl>
      <div class="pp-surface">
        <div class="pp-medium-14" translate="updateCheck.title"></div>

        <div class="pp-medium-12 pp-gray" translate="updateCheck.subTitle"></div>

        <button
          type="button"
          puButton
          size="s"
          uiType="quinary"
          translate="updateCheck.apply"
          (click)="newVersionCheckerService.applyUpdate()"></button>
      </div>
    </ng-template>
  `,
})
export class AppComponent implements OnInit, AfterViewInit {
  readonly #globalToastService = inject(GlobalToastService);
  readonly #mediaScreenService = inject(MediaScreenService);
  readonly #analyticsService = inject(GoogleAnalyticsService);
  readonly #meta = inject(Meta);
  readonly #destroyRef = inject(DestroyRef);
  readonly #configService = inject(ConfigService);
  readonly #pwaSetupDialog = inject(PwaSetupDialog);
  spinner = inject(SpinnerService);
  newVersionCheckerService = inject(NewVersionCheckerService);

  breakpoints$ = combineLatest({
    isDesktop: this.#mediaScreenService.mediaMatcher(CommonMediaQueries.LG),
  });

  isOldIos = this.#pwaSetupDialog.checkOldIos();

  readonly newVersionTpl = viewChild<TemplateRef<any>>('newVersionTpl');
  readonly spinnerDirective = viewChild(SpinnerDirective);

  ngOnInit(): void {
    this.#analyticsService.initialize();
    this.breakpoints$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
      next: ({ isDesktop }) => {
        if (isDesktop) {
          this.#meta.updateTag({ name: 'viewport', content: 'width=device-width, initial-scale=1' }, 'name="viewport"');
        } else {
          this.#meta.updateTag(
            {
              name: 'viewport',
              content: 'width=device-width, initial-scale=1, maximum-scale=1',
            },
            'name="viewport"',
          );
        }
      },
    });

    this.#configService.setUpConfigurations();
    this.#pwaSetupDialog.init();
  }
  ngAfterViewInit(): void {
    this.newVersionCheckerService.isNewVersionAvailable.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((event: boolean) => {
      if (!event) {
        return;
      }

      this.#globalToastService.showCustom({
        tpl: this.newVersionTpl(),
      });
    });
    this.spinnerDirective().disappearing = false;
  }
}
